import React from "react";
import { EventLogEntry } from "@/types/fitconnect.ts";
import { LoadingCard } from "@/components/LoadingCard.tsx";
import { FileX } from "lucide-react";
import { intervalToDuration, parseISO, formatDuration } from "date-fns";
import { de } from "date-fns/locale";
import { reformatDate } from "@/lib/dateTime.tsx";

function classNameForEvent(event: string) {
  switch (event) {
    case "CREATE_SUBMISSION":
    case "SUBMIT_SUBMISSION":
      return "badge-info";
    case "NOTIFY_SUBMISSION":
      return "badge-accent";
    case "ACCEPT_SUBMISSION":
      return "badge-success";
    case "REJECT_SUBMISSION":
      return "badge-error";
    case "DELETE_SUBMISSION":
      return "badge-neutral";
    default:
      return "badge-neutral";
  }
}

function humanReadableTimestampDiff(a: string, b: string): string {
  const start = parseISO(a);
  const end = parseISO(b);

  const duration = intervalToDuration({ start, end });

  const exact = formatDuration(duration, {
    format: ["days", "hours", "minutes", "seconds"],
    locale: de,
  });

  return exact ? `${exact} später` : "0 Sekunden später";
}

export function EventLogList({
  isLoading,
  eventLogEntries,
}: {
  isLoading: boolean;
  eventLogEntries: EventLogEntry[];
}) {
  return (
    <section className="card shadow-sm bg-white h-fit">
      {isLoading && <LoadingCard />}
      <div className="card-body items-center justify-center">
        {eventLogEntries.length == 0 && (
          <div className="flex flex-col items-center justify-center text-gray-500">
            <FileX className="h-8 w-8 mb-2" />
            <p className="text-sm">Keine Daten</p>
          </div>
        )}
        {eventLogEntries.map((entry, index) => (
          <div
            key={entry.eventId}
            className="collapse collapse-arrow bg-base-100 border border-base-300"
          >
            <input
              type="radio"
              name="my-accordion-2"
              defaultChecked={index === 0}
            />
            <div className="collapse-title">
              <div className={`badge mb-4 ${classNameForEvent(entry.event)}`}>
                {entry.event}
              </div>
              <div className="text-slate-600 text-sm">
                {reformatDate(entry.issueTime)}
                {index < eventLogEntries.length - 1 && (
                  <span className="ml-2">
                    (
                    {humanReadableTimestampDiff(
                      eventLogEntries[index + 1].issueTime,
                      entry.issueTime,
                    )}
                    )
                  </span>
                )}
              </div>
            </div>
            <div className="collapse-content text-sm">
              <pre className="whitespace-pre-wrap wrap-break-word rounded-md bg-base-300 p-4 text-sm leading-6 overflow-auto max-h-112">
                {JSON.stringify(entry, null, 2)}
              </pre>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
